import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Alert,
} from "@mui/material";
import {
  Label as LabelIcon,
  Palette as PaletteIcon,
} from "@mui/icons-material";
import TagService from "../services/TagService";
import Tags from "../models/Tags";

const CreateTagDialog = ({
  open,
  onClose,
  onTagCreated,
  tagViewModel,
  editingTag = null,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#1976d2",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const colorOptions = [
    { value: "#f44336", label: "Red", name: "Red" },
    { value: "#e91e63", label: "Pink", name: "Pink" },
    { value: "#9c27b0", label: "Purple", name: "Purple" },
    { value: "#673ab7", label: "Deep Purple", name: "Deep Purple" },
    { value: "#3f51b5", label: "Indigo", name: "Indigo" },
    { value: "#2196f3", label: "Blue", name: "Blue" },
    { value: "#03a9f4", label: "Light Blue", name: "Light Blue" },
    { value: "#00bcd4", label: "Cyan", name: "Cyan" },
    { value: "#009688", label: "Teal", name: "Teal" },
    { value: "#4caf50", label: "Green", name: "Green" },
    { value: "#8bc34a", label: "Light Green", name: "Light Green" },
    { value: "#cddc39", label: "Lime", name: "Lime" },
    { value: "#ffeb3b", label: "Yellow", name: "Yellow" },
    { value: "#ffc107", label: "Amber", name: "Amber" },
    { value: "#ff9800", label: "Orange", name: "Orange" },
    { value: "#ff5722", label: "Deep Orange", name: "Deep Orange" },
    { value: "#795548", label: "Brown", name: "Brown" },
    { value: "#9e9e9e", label: "Grey", name: "Grey" },
    { value: "#607d8b", label: "Blue Grey", name: "Blue Grey" },
  ];

  useEffect(() => {
    if (open) {
      if (editingTag) {
        setFormData({
          name: editingTag.getName(),
          description: editingTag.getDescription(),
          color: editingTag.getColor(),
        });
      } else {
        setFormData({
          name: "",
          description: "",
          color: "#1976d2",
        });
      }
      setErrors({});
    }
  }, [open, editingTag]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Tag name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Tag name must be at least 2 characters";
    } else if (formData.name.trim().length > 50) {
      newErrors.name = "Tag name must be less than 50 characters";
    }

    if (formData.description && formData.description.length > 200) {
      newErrors.description = "Description must be less than 200 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const model = new Tags();
      model.setName(formData.name);
      model.setDescription(formData.description);
      model.setColor(formData.color);
      if (editingTag) {
        model.setId(editingTag.getId())
        await TagService.updateTags(model);
      } else {
        await TagService.createTags(model)
      }
      onTagCreated();
    } catch (error) {
      console.error("Error saving tag:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LabelIcon sx={{ color: "#1976d2" }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            {editingTag ? "Edit Tag" : "Create New Tag"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          {/* Tag Name */}
          <TextField
            label="Tag Name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="Enter tag name (e.g., VIP, Lead, Customer)"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          {/* Tag Description */}
          <TextField
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            error={!!errors.description}
            helperText={
              errors.description ||
              "Brief description of what this tag represents"
            }
            placeholder="Describe the purpose of this tag"
            multiline
            rows={3}
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          {/* Color Selection */}
          <Box>
            <FormLabel
              component="legend"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <PaletteIcon sx={{ fontSize: 20, color: "#666" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}>
                Tag Color
              </Typography>
            </FormLabel>
            <RadioGroup
              value={formData.color}
              onChange={(e) => handleInputChange("color", e.target.value)}
              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
            >
              {colorOptions.map((color) => (
                <FormControlLabel
                  key={color.value}
                  value={color.value}
                  control={
                    <Radio
                      sx={{
                        color: color.value,
                        "&.Mui-checked": {
                          color: color.value,
                        },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: color.value,
                          border: "2px solid #e0e0e0",
                        }}
                      />
                      <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>{color.name}</Typography>
                    </Box>
                  }
                  sx={{
                    margin: 0,
                    padding: 1,
                    borderRadius: 1,
                    border:
                      formData.color === color.value
                        ? `2px solid ${color.value}`
                        : "2px solid transparent",
                    backgroundColor:
                      formData.color === color.value
                        ? `${color.value}10`
                        : "transparent",
                    "&:hover": {
                      backgroundColor: `${color.value}10`,
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Box>

          {/* Preview */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: "#666", fontFamily: 'Poppins, sans-serif'}}>
              Preview:
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  padding: "8px 16px",
                  borderRadius: 2,
                  backgroundColor: formData.color,
                  color: "white",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                }}
              >
                {formData.name || "Tag Name"}
              </Box>
              {formData.description && (
                <Typography variant="body2" color="text.secondary">
                  {formData.description}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !formData.name.trim()}
          sx={{
            backgroundColor: "#1976d2",
            "&:hover": { backgroundColor: "#1565c0" },
            borderRadius: 2,
            px: 3,
          }}
        >
          {isSubmitting
            ? "Saving..."
            : editingTag
            ? "Update Tag"
            : "Create Tag"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTagDialog;
